import { useEffect, useState } from 'react';
import styled from 'styled-components';
import getConfig from 'next/config';
import { Trans } from 'react-i18next';
import { useRouter } from 'next/router';

import { variableFontRegular } from 'styles/theme/text';
import { Modal } from 'shared/components/Modal';
import { useLanguage } from 'shared/hooks/useLanguage';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { requestService } from 'shared/utils/services/requestService';
import { t } from 'localization/utils/t';
import { LOCALES } from 'localization/constants/configEnums';
import { formatLocale } from 'shared/utils/formatLocale';

const TP = 'checkout.components.Payment.KlarnaPlacement';

interface IKlarnaPlacementProps {
  country: string;
  region: string;
  purchaseAmount: number;
}

interface IKlarnaNode {
  name: 'TEXT_MAIN' | 'TEXT_LEGAL' | 'ACTION_LEARN_MORE' | 'ACTION_PREQUALIFY' | 'KLARNA_BADGE';
  type: 'TEXT' | 'ACTION' | 'IMAGE';
  value?: string;
  label?: string;
  url?: string;
  alt?: string;
}

interface IKlarnaOSMResponse {
  content: {
    nodes: IKlarnaNode[];
    // eslint-disable-next-line camelcase
    impression_url: string;
  };
}

export const KlarnaPlacement = ({ country, region, purchaseAmount }: IKlarnaPlacementProps) => {
  const { publicRuntimeConfig } = getConfig();
  const {
    KLARNA_CLIENT_ID_CA,
    KLARNA_CLIENT_ID_EU,
    KLARNA_CLIENT_ID_NA,
    KLARNA_OSM_SERVICE_URL_NA,
    KLARNA_OSM_SERVICE_URL_EU,
  } = publicRuntimeConfig;
  const [nodeByName, setNodeByName] = useState(new Map<string, IKlarnaNode>());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const KLARNA_OSM_API_BY_REGIONS = new Map([
    ['NA', { clientId: KLARNA_CLIENT_ID_NA, apiUrl: KLARNA_OSM_SERVICE_URL_NA }],
    ['EU', { clientId: KLARNA_CLIENT_ID_EU, apiUrl: KLARNA_OSM_SERVICE_URL_EU }],
    ['UK', { clientId: KLARNA_CLIENT_ID_EU, apiUrl: KLARNA_OSM_SERVICE_URL_EU }],
    ['CA', { clientId: KLARNA_CLIENT_ID_CA, apiUrl: KLARNA_OSM_SERVICE_URL_NA }],
  ]);

  const router = useRouter();
  const { selectedLanguage } = useLanguage();
  const locale =
    router?.locale === LOCALES.frFR
      ? formatLocale(LOCALES.frFR)
      : `${selectedLanguage.value}-${country === 'UK' ? 'GB' : country}`;

  const handleLearnMoreClick = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const clearNodeByName = () => {
    setNodeByName(new Map());
  };

  useEffect(() => {
    const { clientId, apiUrl } = KLARNA_OSM_API_BY_REGIONS.get(region) || {};
    if (!clientId || !apiUrl) {
      clearNodeByName();
      return;
    }
    const url =
      `${apiUrl as string}` +
      `?client_id=${clientId as string}` +
      '&placement_key=credit-promotion-auto-size' +
      '&channel=web' +
      `&locale=${locale}` +
      `&purchase_amount=${purchaseAmount}`;
    const fetchOnSiteMessageApi = async () => {
      try {
        // Response provides the tagline's message if any.
        const response: IKlarnaOSMResponse = await requestService({ url });
        const _nodeByName = new Map();
        for (const node of response?.content?.nodes) {
          _nodeByName.set(node.name, node);
        }
        setNodeByName(_nodeByName);
      } catch (error) {
        clearNodeByName();
        bugsnag(error, {
          context: 'Error fetching Klarna On-Site Message (aka OSM) service',
          metadata: {
            locale,
            country,
            region,
            purchaseAmount,
          },
        });
      }
    };
    void fetchOnSiteMessageApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseAmount, locale, region]);

  if (!nodeByName.has('TEXT_MAIN')) {
    return <KlarnaPlacement.Message />;
  }

  return (
    <>
      <KlarnaPlacement.Message data-qa="checkout_payment_klarna_placement">
        <Trans
          i18nKey={`${TP}.klarnaPlacementMessage`}
          defaults="{text1} Klarna. <0>{text2}</0>"
          values={{
            text1: nodeByName.get('TEXT_MAIN')?.value,
            text2: nodeByName.get('ACTION_LEARN_MORE')?.label,
          }}
          components={[
            <KlarnaPlacement.LearnMore
              onClick={handleLearnMoreClick}
              href=""
              data-qa="klarna-learn-more-button"
            />,
          ]}
        />
      </KlarnaPlacement.Message>
      {isModalOpen && (
        <KlarnaPlacement.Modal
          onClose={handleClose}
          title=""
          data-qa="checkout_payment_klarna_placement_modal"
        >
          <iframe
            title={t(`${TP}.adModalTitle`, 'Klarna Ad Modal')}
            src={nodeByName.get('ACTION_LEARN_MORE')?.url}
            sandbox="allow-scripts allow-popups"
            referrerPolicy="no-referrer"
          />
        </KlarnaPlacement.Modal>
      )}
    </>
  );
};

KlarnaPlacement.Message = styled.p`
  ${variableFontRegular({ size: '11.5px' })};
  min-height: 18px;
`;

KlarnaPlacement.LearnMore = styled.a`
  text-decoration: underline;
  white-space: nowrap;
`;

KlarnaPlacement.Modal = styled(Modal)`
  width: 520px;

  iframe {
    width: 100%;
    height: 560px;
  }
`;
